import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTransactions } from '@/hooks/useTransactions';
import { useWallet } from '@/hooks/useWallet';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface WithdrawModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const WithdrawModal = ({ open, onOpenChange }: WithdrawModalProps) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const { createWithdrawal } = useTransactions();
  const { wallet } = useWallet();

  const calculateFee = (amt: number) => {
    return Math.min((amt * 0.01) + 0.25, 10);
  };

  const numAmount = parseFloat(amount || '0');
  const fee = calculateFee(numAmount);
  const total = numAmount + fee;
  const availableBalance = wallet?.balance || 0;

  const handleWithdraw = async () => {
    if (isNaN(numAmount) || numAmount < 10 || total > availableBalance) {
      return;
    }

    setLoading(true);
    try {
      await createWithdrawal(numAmount);
      onOpenChange(false);
      setAmount('');
    } catch (error) {
      console.error('Withdrawal failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Withdraw Funds</DialogTitle>
          <DialogDescription>
            Withdraw funds from your wallet. Minimum withdrawal is $10.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Available balance: <strong>${availableBalance.toFixed(2)}</strong>
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="withdraw-amount">Amount (USD)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                id="withdraw-amount"
                type="number"
                min="10"
                max={availableBalance}
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-7"
              />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Min: $10</span>
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() => setAmount((availableBalance - calculateFee(availableBalance)).toFixed(2))}
              >
                Max available
              </Button>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-muted space-y-2">
            <div className="flex justify-between text-sm">
              <span>Withdrawal amount:</span>
              <span className="font-medium">${numAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Processing fee:</span>
              <span className="font-medium">${fee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-base font-semibold pt-2 border-t">
              <span>Total deduction:</span>
              <span className={total > availableBalance ? 'text-destructive' : ''}>
                ${total.toFixed(2)}
              </span>
            </div>
          </div>

          {total > availableBalance && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Insufficient balance for this withdrawal
              </AlertDescription>
            </Alert>
          )}

          <p className="text-xs text-muted-foreground">
            Processing time: 1-3 business days
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleWithdraw}
            className="flex-1"
            disabled={loading || !amount || numAmount < 10 || total > availableBalance}
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Withdraw
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};